import { useRemoteUsers } from 'agora-rtc-react';
import { useEffect, useMemo, useRef } from 'react';

import { useMeetingData } from '@/app/queries/MeetingQuery/context';

// How long a party may be absent before we stop the feature.
//
// INVARIANT: keep this >= RATE_LIMIT_WINDOW_MS. A restart can only fire once
// the stop has run, i.e. at least this long after the original start, so a
// shorter grace would let a fast drop+rejoin's restart be throttled away —
// silently, since the rate-limit throw is suppressed.
export const STOP_GRACE_MS = 60000; // 60s

export interface CallFeature {
  isActive: boolean;
  // A start has been dispatched but has not resolved yet. `isActive` only flips
  // once the server call returns, so the gate needs this to avoid re-dispatching
  // a start that is already on its way.
  isStartPending: boolean;
  start(): void;
  stop(): void;
}

export default function useAutoCallFeature(
  { joined, isEnabled }: { joined: boolean; isEnabled: boolean },
  feature: CallFeature,
) {
  const remoteUsers = useRemoteUsers();
  const { participants } = useMeetingData();

  // Camera UIDs of the remote (non-host) party. This hook only ever runs
  // host-side, so that party is the customer; excluding screen-share and host
  // UIDs keeps a lone screen share — including the host's own, which joins as a
  // separate remote UID — from counting as presence.
  const remoteCameraUids = useMemo(
    () => new Set(participants.filter(p => !p.is_host).map(p => p.user_id)),
    [participants]
  );

  // `remoteUsers` never includes the local user, so this is the customer's camera.
  const remotePresent = useMemo(
    () => remoteUsers.some(u => remoteCameraUids.has(u.uid as number)),
    [remoteUsers, remoteCameraUids]
  );

  // EITHER side leaving ends "both present": `joined` is the local RTC state
  // (see MeetingPage), so a host departure stops the gate too, not just a
  // customer one.
  const bothPresent = joined && remotePresent;

  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isEnabled) {
      // Gate disabled (e.g. the deliberate call-end path disables it): drop any
      // pending stop so a stale timer can't fire a redundant, rate-limited stop
      // after the prompt End-call stop has already run.
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
      return;
    }

    if (bothPresent) {
      // A party (re)appeared within the grace window: cancel any pending stop
      // (transient-drop tolerance) and start if not already active.
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
      if (!feature.isActive && !feature.isStartPending) {
        feature.start();
      }
      return;
    }

    // Not both present. If active, debounce a stop so a brief blip/rejoin on
    // either side does not tear down and restart the recording.
    if (feature.isActive && !stopTimeoutRef.current) {
      stopTimeoutRef.current = setTimeout(() => {
        stopTimeoutRef.current = null;
        feature.stop();
      }, STOP_GRACE_MS);
    }
  }, [isEnabled, bothPresent, feature]);

  // Clear any pending stop on unmount. No implicit stop here: a deliberate
  // meeting end drives its own prompt stop (see the End call handler), and the
  // server backstop covers an unclean client exit.
  useEffect(() => () => {
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
  }, []);
}
